import type { ReactNode } from "react";

/**
 * Formatted instructions, stored as plain text.
 *
 * A small, fixed subset of Markdown rather than a rich-text editor. Three
 * reasons, in order of how much they matter:
 *
 * 1. What is stored stays READABLE AS TEXT. The audit log records old and new
 *    values of this field, and a diff of two blobs of editor JSON tells you
 *    nothing about what changed.
 * 2. It is what people already type. Someone writing "- check the datum"
 *    gets a bullet whether or not they knew the rule.
 * 3. No dependency, and nothing to migrate if we change our minds.
 *
 * Rendered to React ELEMENTS, never to an HTML string. There is no
 * dangerouslySetInnerHTML anywhere in here, so a note containing a script tag
 * is just a note containing a script tag.
 */

export interface RichTextProps {
  text: string;
  /**
   * Called with the line index when a checkbox is clicked. Omit to render
   * the boxes disabled — a checklist you cannot tick is worse than no
   * checklist, so anywhere they are shown live this should be provided.
   */
  onToggleCheck?: (lineIndex: number) => void;
}

const CHECKED = /^(\s*)[-*]\s+\[[xX]\]\s+(.*)$/;
const UNCHECKED = /^(\s*)[-*]\s+\[\s?\]\s+(.*)$/;
const BULLET = /^(\s*)[-*]\s+(.*)$/;
const NUMBERED = /^(\s*)(\d+)[.)]\s+(.*)$/;

/** Flips "- [ ] x" to "- [x] x" and back, leaving everything else alone. */
export function toggleCheckAtLine(text: string, lineIndex: number): string {
  const lines = text.split("\n");
  const line = lines[lineIndex];
  if (line === undefined) return text;

  if (CHECKED.test(line)) {
    lines[lineIndex] = line.replace(/\[[xX]\]/, "[ ]");
  } else if (UNCHECKED.test(line)) {
    lines[lineIndex] = line.replace(/\[\s?\]/, "[x]");
  }
  return lines.join("\n");
}

/**
 * Inline marks: **bold**, *italic*, `code`, [text](url) and bare links.
 *
 * One pass with alternation rather than nested replaces, so "**bold**" inside
 * a link, or an asterisk inside code, cannot be mangled by a later rule.
 */
// Triple first: "***x***" is bold AND italic, and the two-asterisk rule would
// otherwise match "**" and leave a stray asterisk either side.
const INLINE =
  /(\*\*\*[^*\n]+\*\*\*)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\s]+\))|((?:https?:\/\/|www\.)[^\s<>]+)/g;

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;

  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const token = m[0];
    const key = `${keyBase}-${n++}`;

    if (token.startsWith("***")) {
      out.push(
        <strong key={key}>
          <em>{token.slice(3, -3)}</em>
        </strong>,
      );
    } else if (token.startsWith("**")) {
      out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      out.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("[")) {
      const cut = token.indexOf("](");
      const label = token.slice(1, cut);
      const href = token.slice(cut + 2, -1);
      out.push(
        <a key={key} href={safeHref(href)} target="_blank" rel="noreferrer noopener">
          {label}
        </a>,
      );
    } else if (token.startsWith("http") || token.startsWith("www.")) {
      out.push(
        <a key={key} href={safeHref(token)} target="_blank" rel="noreferrer noopener">
          {token}
        </a>,
      );
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }

    last = at + token.length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * Only http(s) links are rendered as links.
 *
 * Without this, "[click me](javascript:…)" in a task someone else wrote would
 * be a working script link. Anything else renders as plain text, which is the
 * safe failure.
 */
function safeHref(href: string): string {
  const trimmed = href.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  return "#";
}

interface Block {
  kind: "p" | "ul" | "ol" | "checks";
  items: { text: string; line: number; checked?: boolean }[];
}

function parse(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.split("\n");

  const push = (kind: Block["kind"], item: Block["items"][number]): void => {
    const open = blocks[blocks.length - 1];
    if (open && open.kind === kind) open.items.push(item);
    else blocks.push({ kind, items: [item] });
  };

  lines.forEach((raw, i) => {
    if (raw.trim() === "") {
      // A blank line ends whatever was open, so two lists separated by space
      // stay two lists.
      blocks.push({ kind: "p", items: [] });
      return;
    }

    const checked = CHECKED.exec(raw);
    if (checked) return push("checks", { text: checked[2] ?? "", line: i, checked: true });

    const unchecked = UNCHECKED.exec(raw);
    if (unchecked)
      return push("checks", { text: unchecked[2] ?? "", line: i, checked: false });

    const numbered = NUMBERED.exec(raw);
    if (numbered) return push("ol", { text: numbered[3] ?? "", line: i });

    const bullet = BULLET.exec(raw);
    if (bullet) return push("ul", { text: bullet[2] ?? "", line: i });

    push("p", { text: raw, line: i });
  });

  return blocks.filter((b) => b.items.length > 0);
}

export function RichText({ text, onToggleCheck }: RichTextProps) {
  const blocks = parse(text);

  return (
    <div className="rt">
      {blocks.map((b, i) => {
        if (b.kind === "ul") {
          return (
            <ul key={i}>
              {b.items.map((it) => (
                <li key={it.line}>{inline(it.text, `i${it.line}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.kind === "ol") {
          return (
            <ol key={i}>
              {b.items.map((it) => (
                <li key={it.line}>{inline(it.text, `i${it.line}`)}</li>
              ))}
            </ol>
          );
        }
        if (b.kind === "checks") {
          return (
            <ul key={i} className="rt-checks">
              {b.items.map((it) => (
                <li key={it.line} className={it.checked ? "done" : ""}>
                  <input
                    type="checkbox"
                    checked={Boolean(it.checked)}
                    disabled={!onToggleCheck}
                    onChange={() => onToggleCheck?.(it.line)}
                  />
                  <span>{inline(it.text, `i${it.line}`)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i}>
            {b.items.map((it, j) => (
              <span key={it.line}>
                {j > 0 ? <br /> : null}
                {inline(it.text, `i${it.line}`)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
